import { useMemo } from "react";
import { ReportSettingRowVisibility } from "@common/types/reports/report-settings";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { formatMoneyColumnHeader } from "@common/helpers/number";
import {
    hasTrialBalanceAmounts,
    PreviewTrialBalanceDataset,
} from "./previewTypes";
import { filterTrialBalanceRows } from "./filterPreviewRows";
import { previewRowPercent } from "./previewPercent";
import { AmountCell } from "./AmountCell";
import { PercentCell } from "./PercentCell";
import styles from "./ReportPreview.module.scss";

interface Props {
    dataset: PreviewTrialBalanceDataset;
    numberFormat: NumberFormatValues;
    rowVisibility: ReportSettingRowVisibility;
}

export const TrialBalanceTable = ({
    dataset,
    numberFormat,
    rowVisibility,
}: Props) => {
    const rows = useMemo(
        () => filterTrialBalanceRows(dataset.rows, rowVisibility),
        [dataset.rows, rowVisibility],
    );

    return (
        <div className={styles.table}>
            <div className={styles.columnHeader} data-columns="trial-balance">
                <span>{dataset.accountHeader}</span>
                <span className={styles.amountCell}>
                    {formatMoneyColumnHeader(dataset.debitHeader, numberFormat)}
                </span>
                <span className={styles.amountCell}>
                    {formatMoneyColumnHeader(
                        dataset.creditHeader,
                        numberFormat,
                    )}
                </span>
                <span className={styles.percentCell}>
                    {dataset.percentHeader}
                </span>
            </div>
            {rows.map((row) => {
                if (row.kind === "spacer") {
                    return <div key={row.id} className={styles.spacerRow} />;
                }
                const { debit, credit } = hasTrialBalanceAmounts(row)
                    ? row
                    : { debit: undefined, credit: undefined };
                const percent = previewRowPercent({
                    amount: debit === 0 ? credit : debit,
                    percentBase: dataset.percentBase,
                });
                return (
                    <div
                        key={row.id}
                        className={styles.gridRow}
                        data-columns="trial-balance"
                        data-kind={row.kind}
                        data-top-border={row.hasTopBorder || undefined}
                    >
                        <span>{row.label}</span>
                        <AmountCell value={debit} numberFormat={numberFormat} />
                        <AmountCell
                            value={credit}
                            numberFormat={numberFormat}
                        />
                        <PercentCell
                            value={percent}
                            numberFormat={numberFormat}
                        />
                    </div>
                );
            })}
        </div>
    );
};
